// Feature 3 (requested 2026-10-18): when the initiative turn LANDS ON a token the
// tracker cannot place, ask the DM what it is — associate it with a creature,
// refresh the monster catalogue, make it an effect timer, make it an event
// countdown, or dismiss it ("select none"). The dialogue and the turn-landing
// trigger live in the components (modelled on RideKeeper); this module is the
// pure, testable core: the predicate that decides a row is worth asking about,
// and the campaign-scoped store that remembers a "don't ask again" dismissal.
//
// The honesty rule here is NOT to interrupt the table for nothing. A PC, a known
// monster, a countdown the DM made on purpose — none of those is "unknown", and a
// dialogue that pops for them mid-combat is worse than no feature at all. So the
// predicate is deliberately narrow, and a dismissal is remembered per token.
import type { Layout } from "./types";
import { allyKey } from "./conditions";

export type UnknownOutcome = "creature" | "catalogue" | "effect" | "countdown" | "dismiss";

/* ---- the "don't ask again" store: campaign-scoped, keyed by allyKey, exactly
   like roles (riders.ts) and conditions/defences. Survives a token re-drop by
   name, and withUnknownDismissed(l, key, false) clears it. ---- */
const KEY = "unknownTokens";
type DismissMap = Record<string, Record<string, string>>;
export function unknownDismissed(layout: Layout): Record<string, string> {
  return ((layout[KEY] as DismissMap | undefined)?.[layout.activeCampaign]) || {};
}
export function withUnknownDismissed(layout: Layout, key: string, dismissed: boolean): Layout {
  const all: DismissMap = { ...((layout[KEY] as DismissMap | undefined) || {}) };
  const mine = { ...(all[layout.activeCampaign] || {}) };
  if (dismissed) mine[key] = "dismissed"; else delete mine[key];
  all[layout.activeCampaign] = mine;
  return { ...layout, [KEY]: all };
}

/* The row the turn landed on — a subset of the initiative `entries` item. */
export interface TurnRow { id: string; name: string; kind: string; tokenId?: string; custom?: boolean; timer?: boolean; source?: string }

/** Has the DM already said "don't ask" for this token, this campaign? */
export const isDismissed = (e: TurnRow, dismissed: Record<string, string>): boolean => !!dismissed[allyKey(e)];

/** Is this landed-on row an "unknown token" the DM should be asked about?
 *
 *  This is the SYNCHRONOUS gate. `boundToPc` is whether the caller resolved this
 *  token to a party member (characterForToken !== null); `dismissed` is the
 *  campaign's don't-ask map (keys are allyKey). The caller does the async part —
 *  a statblock/catalogue lookup (creatureBlock) — only when this returns true, so
 *  keep this cheap and conservative: a false positive is a pop-up for nothing. */
export function isUnknownToken(e: TurnRow, opts: { boundToPc: boolean; dismissed: Record<string, string> }): boolean {
  // Conservative, cheapest checks first — this runs on every turn change, and a
  // false positive is a pop-up for nothing. Each gate bails to false; only a
  // named, placed token that is not a PC, not a DM-made timer, and not already
  // dismissed survives to the (async) statblock lookup the caller does next.
  if (e.kind === "pc" || opts.boundToPc) return false;   // a party member is known
  if (e.timer || e.source === "timer") return false;     // a countdown the DM made, not a creature
  if (!e.tokenId || e.tokenId === "-1") return false;    // not a real Roll20 map token
  if (!String(e.name || "").trim()) return false;        // nothing to search on
  if (isDismissed(e, opts.dismissed)) return false;      // the DM already said don't ask
  return true;
}
